var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useState, } from 'react';
import { STATIC_BASE_URL } from '../../design-tokens/style-utilities';
import { NavigationAction } from './components/NavigationAction';
import { NavigationActionSearch } from './components/NavigationActionSearch';
import { NavigationActionSheet } from './components/NavigationActionSheet';
import { NavigationActionSheetItem } from './components/NavigationActionSheetItem';
import { NavigationActionSheetItems } from './components/NavigationActionSheetItems';
import { NavigationActionSheetLink } from './components/NavigationActionSheetLink';
import { NavigationActionSheetLinkIcon } from './components/NavigationActionSheetLinkIcon';
import { NavigationActionSheetSearch } from './components/NavigationActionSheetSearch';
import { NavigationActionSheetTitle } from './components/NavigationActionSheetTitle';
import { NavigationActionToggle } from './components/NavigationActionToggle';
import { NavigationActionToggleLogin } from './components/NavigationActionToggleLogin';
import { NavigationActionToggleSearch } from './components/NavigationActionToggleSearch';
import { NavigationActions } from './components/NavigationActions';
import { NavigationDivider } from './components/NavigationDivider';
import { NavigationFlyout } from './components/NavigationFlyout';
import { NavigationFlyoutAside } from './components/NavigationFlyoutAside';
import { NavigationFlyoutColumn } from './components/NavigationFlyoutColumn';
import { NavigationFlyoutRow } from './components/NavigationFlyoutRow';
import { NavigationItem } from './components/NavigationItem';
import { NavigationItems } from './components/NavigationItems';
import { NavigationLink } from './components/NavigationLink';
import { NavigationLinkBadge } from './components/NavigationLinkBadge';
import { NavigationLinkLabel } from './components/NavigationLinkLabel';
import { NavigationLogo } from './components/NavigationLogo';
import { NavigationMain } from './components/NavigationMain';
import { NavigationSecondary } from './components/NavigationSecondary';
import { NavigationSkipLink } from './components/NavigationSkipLink';
import { NavigationSubItem } from './components/NavigationSubItem';
import { NavigationSubItemLink } from './components/NavigationSubItemLink';
import { NavigationSubItems } from './components/NavigationSubItems';
import { NavigationSubItemsToggle } from './components/NavigationSubItemsToggle';
import { NavigationToggle } from './components/NavigationToggle';
import { NAVIGATION_HEIGHT } from './constants/navigation-css-variables';
import { NavigationContext } from './provider';
import { NavigationContainer, NavigationInner } from './styles/navigation.styled';
const Navigation = (_a) => {
    var { children, dataTest = 'navigation' } = _a, props = __rest(_a, ["children", "dataTest"]);
    const navigationRef = useRef(null);
    const heightRef = useRef(0);
    const { navigationIsOpen, setActiveItemReference, activeActionReference, setActiveActionReference, navigationViewportBreakpoint, } = useContext(NavigationContext);
    const [isPrintMedia, setIsPrintMedia] = useState(false);
    useEffect(() => {
        const handleKeydown = (e) => {
            if (e.key === 'Escape') {
                setActiveItemReference(null);
                setActiveActionReference(null);
            }
        };
        window.addEventListener('keydown', handleKeydown);
        return () => {
            window.removeEventListener('keydown', handleKeydown);
        };
    }, [setActiveActionReference, setActiveItemReference]);
    useEffect(() => {
        const navigationElement = navigationRef.current;
        const resizeObserver = new ResizeObserver(([entry]) => {
            const updatedHeight = entry.target.offsetHeight;
            /**
             * Going into print mode will cause the height of the page which is not in print mode to be 0.
             * Unfortunately, the ResizeObserver will not be called when going out of print mode. Also, the matches property of the MediaQueryListEvent is inreliable.
             * Therefore, we don't update the height when the height is 0 which is sensible in general.
             */
            if (updatedHeight > 0 && heightRef.current !== updatedHeight) {
                document.body.style.setProperty(NAVIGATION_HEIGHT, `${updatedHeight}px`);
                heightRef.current = updatedHeight;
            }
        });
        if (navigationElement) {
            resizeObserver.observe(navigationElement);
        }
        return () => {
            if (resizeObserver && navigationElement) {
                resizeObserver.unobserve(navigationElement);
            }
        };
    }, []);
    useEffect(() => {
        const isMobileViewports = ['xxs', 'xs', 'sm', 'md'].includes(navigationViewportBreakpoint);
        if ((navigationIsOpen || activeActionReference) && isMobileViewports) {
            document.body.style.overflow = 'hidden scroll';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
        }
        else {
            document.body.style.overflow = '';
            document.body.style.position = 'relative';
            document.body.style.width = 'initial';
        }
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = 'relative';
            document.body.style.width = 'initial';
        };
    }, [navigationIsOpen, activeActionReference]);
    useEffect(() => {
        const mediaQueryList = window.matchMedia('print');
        const handleMediaQueryChange = (e) => {
            setIsPrintMedia(e.matches);
        };
        mediaQueryList.addEventListener('change', handleMediaQueryChange);
        return () => {
            mediaQueryList.removeEventListener('change', handleMediaQueryChange);
        };
    }, []);
    // Check if browser is in print mode and then only render the ANWB logo
    if (isPrintMedia) {
        return (_jsx(NavigationLogo, { alt: "Logo van de ANWB", href: "/", src: `${STATIC_BASE_URL}/poncho/navigation/images/logo.svg`, id: "navigation-logo-link-print", children: "ANWB Homepage" }));
    }
    return (_jsx(NavigationContainer, Object.assign({ "$isOpen": navigationIsOpen, "data-test": dataTest, ref: navigationRef }, props, { children: _jsx(NavigationInner, { children: children }) })));
};
Navigation.Action = NavigationAction;
Navigation.Actions = NavigationActions;
Navigation.ActionSearch = NavigationActionSearch;
Navigation.ActionSheet = NavigationActionSheet;
Navigation.ActionSheetItem = NavigationActionSheetItem;
Navigation.ActionSheetItems = NavigationActionSheetItems;
Navigation.ActionSheetLink = NavigationActionSheetLink;
Navigation.ActionSheetLinkIcon = NavigationActionSheetLinkIcon;
Navigation.ActionSheetSearch = NavigationActionSheetSearch;
Navigation.ActionSheetTitle = NavigationActionSheetTitle;
Navigation.ActionToggle = NavigationActionToggle;
Navigation.ActionToggleLogin = NavigationActionToggleLogin;
Navigation.ActionToggleSearch = NavigationActionToggleSearch;
Navigation.Divider = NavigationDivider;
Navigation.Flyout = NavigationFlyout;
Navigation.FlyoutAside = NavigationFlyoutAside;
Navigation.FlyoutColumn = NavigationFlyoutColumn;
Navigation.FlyoutRow = NavigationFlyoutRow;
Navigation.Item = NavigationItem;
Navigation.Items = NavigationItems;
Navigation.Link = NavigationLink;
Navigation.LinkBadge = NavigationLinkBadge;
Navigation.LinkLabel = NavigationLinkLabel;
Navigation.Logo = NavigationLogo;
Navigation.Main = NavigationMain;
Navigation.Secondary = NavigationSecondary;
Navigation.SkipLink = NavigationSkipLink;
Navigation.SubItem = NavigationSubItem;
Navigation.SubItemLink = NavigationSubItemLink;
Navigation.SubItems = NavigationSubItems;
Navigation.SubItemsToggle = NavigationSubItemsToggle;
Navigation.Toggle = NavigationToggle;
export { Navigation };
